import * as mutations from './mutation-types'
import * as actions from './action-types'
import * as constants from '../constants'
import { Message } from '@/model/messages'
import {
  createdBy,
  dashboardUrl,
  parentUrlParam,
  privacyAgreementAccepted,
  tenantHash
} from '@/app.config'
import { retrieveCookieUserId } from '@/helpers/cookies'
import { getSessionId } from '@/helpers/keycloak'
import {
  birthDate,
  contractId,
  conversationId,
  email,
  estateId,
  referenceTenant,
  tenantId
} from '../app.config'
import debounce from 'lodash.debounce'
import { getWithExpiry, setWithExpiry } from '@/helpers/sessionStorage'
import {
  getParentDomain,
  isCRMLoginPage,
  isProbablyCRM,
  normalizeBoolean
} from '@/store/util'
import {
  getCustomData,
  isStartMessage,
  messageHasSmartDocSegmentTooltips
} from '@/helpers/util'
import { addDocumentLinkToMessage } from '@/helpers/copilot'

const { v4: uuidv4 } = require('uuid')
const urlParams = new URLSearchParams(window.location.search)

function parseJson(attachment) {
  let parsedAttachment = attachment

  // If attachment is already an object (not a string), return it as-is
  if (typeof attachment === 'object' && attachment !== null) {
    return attachment
  }

  // If it's not a string, return it as-is (could be null, undefined, number, etc.)
  if (typeof attachment !== 'string') {
    return attachment
  }

  // Try to parse the JSON string
  try {
    parsedAttachment = JSON.parse(attachment)
  } catch (e) {
    global.log.error(
      `Failed to parse attachment: ${attachment}. Error: ${e.message}`
    )
    // If parsing fails, we keep the original attachment as a fallback
    parsedAttachment = attachment
  }
  return parsedAttachment
}

const lastIndexOfBotMessage = messages => {
  let index = [...messages]
    .reverse()
    .findIndex(message => message.type === constants.AUTHOR_BOT)
  return index >= 0 ? messages.length - 1 - index : index
}

const getNumberOfUserMessages = messages => {
  const userMessages = []
  messages.forEach(function(message) {
    if (
      message.text.startsWith('EXTERNAL: ') ||
      (message.text.startsWith('/') &&
        !(
          message.metadata &&
          message.metadata.user_message &&
          !message.metadata.user_message.startsWith('/')
        ))
    ) {
      return
    }
    if (message.hasOwnProperty('type') && message.type === ('user' || 'me')) {
      userMessages.push(message)
    }
  })

  return userMessages
}

function getSeparateMessages(message, arrayWithSplitHtml) {
  const separateMessages = []
  for (let i = 0; i < arrayWithSplitHtml.length; i++) {
    if (i < arrayWithSplitHtml.length - 1) {
      const newMessage = {
        author: message.author,
        created: message.created,
        id: uuidv4(),
        received: message.received,
        sessionId: message.sessionId,
        timestamp: message.timestamp,
        type: 'text'
      }
      newMessage['text'] = arrayWithSplitHtml[i]
      separateMessages.push(newMessage)
    } else {
      const newMessage = { ...message, id: uuidv4() }
      newMessage['text'] = arrayWithSplitHtml[i]
      separateMessages.push(newMessage)
    }
  }
  return separateMessages
}

function handlePreviousMessages(store, messages, sessionId) {
  let parsed = []
  let lastBotMessageIdx = lastIndexOfBotMessage(messages)
  let userMessages = getNumberOfUserMessages(messages)
  let lastSmartDocSegmentTooltips = null
  messages.forEach(function(message, idx) {
    if (message.hasOwnProperty('type')) {
      let type = message.type === 'user' ? constants.AUTHOR_USER : message.type
      delete message.type

      // skip intent messages that are not a button payload with a user_message
      if (
        message.text.startsWith('EXTERNAL: ') ||
        (message.text.startsWith('/') &&
          !(
            message.metadata &&
            message.metadata.user_message &&
            !message.metadata.user_message.startsWith('/')
          ))
      ) {
        return
      }
      if (type === constants.AUTHOR_USER) {
        parseMessage(type, sessionId, message)
        parsed.push(message)
      } else if (type === constants.AUTHOR_BOT) {
        let arrayWithSplitHtml = []

        if (isStartMessage(message)) {
          arrayWithSplitHtml = message['text'].split('\n\n')
        } else {
          const regex = /(?:\n\s*<br\s*\/?>\s*){2,}/gi
          arrayWithSplitHtml = message['text']
            .split(regex)
            .filter(bubble => bubble.trim() !== '')
        }

        const onlyDisplayLastBubbleMessage =
          message['metadata'] &&
          message['metadata']['custom_info'] &&
          message['metadata']['custom_info']['only_last_bubble_message']

        if (message['attachment']) {
          if (message['text']) {
            const newMessage = {
              author: message.author,
              created: message.created,
              id: uuidv4(),
              received: message.received,
              sessionId: message.sessionId,
              timestamp: message.timestamp,
              type: 'text'
            }
            newMessage['text'] = message.text
            parseMessage(type, sessionId, newMessage)
            parsed.push(newMessage)
          }

          message.attachment = parseJson(message.attachment)
          parseMessage(type, sessionId, message)
          parsed.push(message)
          return
        }

        if (!userMessages.length) {
          applyMessageDataConfigurations(store, message)
        }

        let newMessage
        if (arrayWithSplitHtml.length > 1) {
          if (onlyDisplayLastBubbleMessage) {
            const tmpMessage = { ...message, id: uuidv4() }
            tmpMessage['text'] =
              arrayWithSplitHtml[arrayWithSplitHtml.length - 1]
            newMessage = tmpMessage
          } else {
            newMessage = getSeparateMessages(message, arrayWithSplitHtml)
          }
        } else {
          newMessage = message
        }

        const smartDoc = store.state.isCopilotChat
        const documentId = urlParams.get('document_id')
        const collectionId = urlParams.get('collection_id')
        if (newMessage.length) {
          for (let i = 0; i < newMessage.length; i++) {
            if (
              (documentId || collectionId || smartDoc) &&
              type === constants.AUTHOR_BOT &&
              messageHasSmartDocSegmentTooltips(newMessage[i]) &&
              !isStartMessage(newMessage[i])
            ) {
              const customData = getCustomData(newMessage[i])
              lastSmartDocSegmentTooltips = customData
              addDocumentLinkToMessage(
                customData,
                newMessage[i],
                store,
                smartDoc
              )
            } else if (lastSmartDocSegmentTooltips) {
              addDocumentLinkToMessage(
                lastSmartDocSegmentTooltips,
                newMessage[i],
                store,
                smartDoc
              )
            }
            if (idx === lastBotMessageIdx && !!userMessages.length) {
              handleMessage(store, newMessage[i])
            } else {
              parseMessage(type, sessionId, newMessage[i])
              parsed.push(newMessage[i])
            }
          }
        } else {
          if (
            (documentId || collectionId || smartDoc) &&
            idx !== lastBotMessageIdx &&
            type === constants.AUTHOR_BOT &&
            messageHasSmartDocSegmentTooltips(newMessage)
          ) {
            const customData = getCustomData(newMessage)
            lastSmartDocSegmentTooltips = customData
            addDocumentLinkToMessage(customData, newMessage, store, smartDoc)
          } else if (lastSmartDocSegmentTooltips) {
            addDocumentLinkToMessage(
              lastSmartDocSegmentTooltips,
              newMessage,
              store,
              smartDoc
            )
          }
          if (idx === lastBotMessageIdx && !!userMessages.length) {
            handleMessage(store, newMessage)
          } else {
            parseMessage(type, sessionId, newMessage)
            parsed.push(newMessage)
          }
        }
      }
    }
  })

  if (lastSmartDocSegmentTooltips) {
    store.commit(
      mutations.SET_LAST_SMARTDOC_SEGMENT_TOOLTIPS,
      lastSmartDocSegmentTooltips
    )
  }

  return parsed
}

export function parseMessage(author, sessionId, data) {
  Object.setPrototypeOf(data, Message.prototype)
  data.author = author
  data.sessionId = sessionId || getWithExpiry('currentSession', tenantHash)
  data.id = data.id ? data.id : data.getNewMessageId()
  if (data.metadata && data.metadata.user_message) {
    data.text = data.metadata.user_message
  }
  data.created = Date.now()
  data.received = Date.now()
  data.type = data.getType()

  return data
}

function messageHasAllowUserInputFlag(message) {
  return (
    (message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty('allowUserInput') &&
      message['custom']['data']['allowUserInput']) ||
    (message['config'] &&
      message['config'].hasOwnProperty('data') &&
      message['config']['data'].hasOwnProperty('allowUserInput') &&
      message['config']['data']['allowUserInput'])
  )
}

function messageHasAllowFlag(message, flag) {
  return (
    (message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty(flag) &&
      message['custom']['data'][flag]) ||
    (message['config'] &&
      message['config'].hasOwnProperty('data') &&
      message['config']['data'].hasOwnProperty(flag) &&
      message['config']['data'][flag])
  )
}

window.goToSource = function(data) {
  window.parent.postMessage(data, '*')
}
function applyMessageDataConfigurations(store, message, customData) {
  if (!customData) {
    customData = getCustomData(message)
  }

  if (customData.hasOwnProperty('suggestions')) {
    message.suggestions = parseJson(customData.suggestions)
    debouncedSuggestionsDispatch(store, 'UPDATE_SUGGESTIONS', customData)
  } else if (customData.hasOwnProperty('semanticSuggestions')) {
    message.suggestions = parseJson(customData.semanticSuggestions)
    debouncedSuggestionsDispatch(
      store,
      'UPDATE_SEMANTIC_SUGGESTIONS',
      customData
    )
  } else {
    debouncedSuggestionsDispatch(store, 'UPDATE_SUGGESTIONS', null)
  }
  if (customData.hasOwnProperty('allowFileUpload')) {
    store.dispatch(
      actions.SET_FILE_UPLOAD_ENABLED,
      normalizeBoolean(customData['allowFileUpload'])
    )
    debouncedInputEnabledDispatch(store, true)
  } else {
    store.dispatch(actions.SET_FILE_UPLOAD_ENABLED, false)
  }
  if (
    customData.hasOwnProperty('allowUserInput') ||
    (message.hasOwnProperty('custom') && message['custom'])
  ) {
    let shouldAllowUserInput = customData['allowUserInput']
    if (typeof customData['allowUserInput'] === 'string') {
      shouldAllowUserInput = customData['allowUserInput'] === 'true'
    }

    if (shouldAllowUserInput) {
      debouncedInputEnabledDispatch(store, true)
    } else {
      debouncedInputEnabledDispatch(store, false)
    }
  } else if (customData.hasOwnProperty('emotion')) {
    // store.dispatch(actions.SET_EMOTION, message['emotion'])
  }
  if (customData.hasOwnProperty('allowBackDisabled')) {
    store.dispatch(actions.SET_BACK_DISABLED, customData['allowBackDisabled'])
  }
  if (message.hasOwnProperty('occupied_dates')) {
    store.dispatch(actions.SET_OCCUPIED_DATES, message['occupied_dates'])
  } else if (!customData.hasOwnProperty('allowUserInput')) {
    store.dispatch(actions.SET_OCCUPIED_DATES, [])
    if (!customData.hasOwnProperty('suggestions')) {
      store.dispatch(actions.UPDATE_SUGGESTIONS, null)
    }
  }
  const smartDoc = store.state.isCopilotChat
  if (messageHasSmartDocSegmentTooltips(message)) {
    addDocumentLinkToMessage(customData, message, store, smartDoc)
  } else if (store.state.lastSmartdocSegmentTooltips) {
    addDocumentLinkToMessage(
      store.state.lastSmartdocSegmentTooltips,
      message,
      store,
      smartDoc
    )
  }

  if (customData.hasOwnProperty('allowSemanticSuggestions')) {
    let enabled = customData['allowSemanticSuggestions']
    if (typeof customData['allowSemanticSuggestions'] === 'string') {
      enabled = customData['allowSemanticSuggestions'] === 'true'
    }

    debouncedSemanticSuggestionsEnabledDispatch(store, enabled)
  }

  if (
    !message.hasOwnProperty('allowUserInput') &&
    !(
      message.hasOwnProperty('custom') &&
      message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty('allowUserInput')
    )
  ) {
    if (!messageHasAllowUserInputFlag(message)) {
      debouncedInputEnabledDispatch(store, true)
    }
  }

  if (
    !customData.hasOwnProperty('allowSemanticSuggestions') &&
    !(
      message.hasOwnProperty('custom') &&
      message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty('allowSemanticSuggestions')
    )
  ) {
    if (!messageHasAllowFlag(message, 'allowSemanticSuggestions')) {
      debouncedSemanticSuggestionsEnabledDispatch(store, true)
    }
  }
}

function handleMessage(store, message) {
  // store.dispatch(actions.SET_FILE_UPLOAD_ENABLED, false)
  let customData = message
  let isSingleMessage = false

  // TODO refactor custom message stuff
  if (message.showFeedbackPrompt && Object.keys(message).length === 1) return

  if (
    message.hasOwnProperty('metadata') &&
    message['metadata'].hasOwnProperty('isPart') &&
    message.metadata['isPart'] === true
  ) {
    if (store.state.socket.messageList.slice(-1)[0].id === message.id) {
      message.text =
        store.state.socket.messageList.slice(-1)[0].text + message.text
    }

    store.dispatch(
      actions.ADD_BOT_MESSAGE_PART,
      parseMessage(constants.AUTHOR_BOT, store.state.sessionId, message)
    )
    return
  }

  if (
    message.hasOwnProperty('custom') &&
    message['custom'] &&
    message['custom'].hasOwnProperty('data')
  ) {
    customData = message['custom']['data']
    isSingleMessage = true
  } else if (
    message.hasOwnProperty('config') &&
    message['config'] &&
    message['config'].hasOwnProperty('data')
  ) {
    customData = message['config']['data']
    isSingleMessage = true
  }
  store.dispatch(actions.SET_BACK_DISABLED, false)

  if (
    message.hasOwnProperty('metadata') &&
    message['metadata']['role'] &&
    message['metadata']['role'] === 'operator'
  ) {
    message['author'] = constants.AUTHOR_OPERATOR
    message['avatar'] = store.state.socket.operatorAvatarSrc
    store.dispatch(
      actions.ADD_OPERATOR_MESSAGE,
      parseMessage(constants.AUTHOR_OPERATOR, store.state.sessionId, message)
    )
  } else {
    applyMessageDataConfigurations(store, message, customData)
    if (
      customData.hasOwnProperty('showFeedbackSuggestionButton') &&
      global.realm_tenant.toLowerCase() !== 'degewo'
    ) {
      if (message.quick_replies) {
        const quickRepliesCopy = [...message.quick_replies]
        quickRepliesCopy.push({
          config: null,
          content_type: 'text',
          payload: 'feedback',
          title: 'giveFeedback',
          tooltip: null,
          feedbackSuggestionButton: true
        })
        message.quick_replies = quickRepliesCopy
      }
    }
    if (
      !message.hasOwnProperty('allowSemanticSuggestions') &&
      !message.hasOwnProperty('allowUserInput') &&
      !customData.hasOwnProperty('suggestions') &&
      !message.hasOwnProperty('occupied_dates') &&
      !message.hasOwnProperty('allowBackDisabled') &&
      !(
        customData.hasOwnProperty('allowUserInput') ||
        (message.hasOwnProperty('custom') && message['custom']) ||
        (message.hasOwnProperty('config') && message['config'])
      )
    ) {
      if (message.attachment) {
        message.attachment = parseJson(message.attachment)
      }
      store.dispatch(
        actions.ADD_BOT_MESSAGE,
        parseMessage(constants.AUTHOR_BOT, store.state.sessionId, message)
      )
    }
  }

  if (
    !customData.hasOwnProperty('allowSemanticSuggestions') &&
    !(
      message.hasOwnProperty('custom') &&
      message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty('allowSemanticSuggestions')
    )
  ) {
    // TODO set / clear timeout with vuex store
    if (!messageHasAllowFlag(message, 'allowSemanticSuggestions')) {
      debouncedSemanticSuggestionsEnabledDispatch(store, true)
    }
  }

  if (
    !message.hasOwnProperty('allowUserInput') &&
    !(
      message.hasOwnProperty('custom') &&
      message['custom'] &&
      message['custom'].hasOwnProperty('data') &&
      message['custom']['data'].hasOwnProperty('allowUserInput')
    )
  ) {
    if (!messageHasAllowUserInputFlag(message)) {
      debouncedInputEnabledDispatch(store, true)
    }
  }
  if (isSingleMessage) {
    store.dispatch(
      actions.ADD_BOT_MESSAGE,
      parseMessage(constants.AUTHOR_BOT, store.state.sessionId, message)
    )
  }
}

const debouncedInputEnabledDispatch = debounce(async function(store, state) {
  store.dispatch(actions.SET_INPUT_ENABLED, state)
}, 500)

const debouncedSemanticSuggestionsEnabledDispatch = debounce(async function(
  store,
  state
) {
  store.dispatch(actions.SET_SEMANTIC_SUGGESTIONS_ENABLED, state)
},
500)

const debouncedSuggestionsDispatch = debounce(async function(
  store,
  dispatchAction,
  customData
) {
  store.dispatch(actions[dispatchAction], customData)
},
500)

export default function createB4uSocketPlugin(socket) {
  async function emitNewUserMessage(message, state) {
    addMetaData(message, state)
    let sessionId = getSessionId(state)
    global.log.info(`emitNewUserMessage ${sessionId}`)

    const documentId = urlParams.get('document_id')
    if (documentId) {
      const data = {
        message: 'messageSent',
        documentId: documentId,
        time: new Date().toISOString()
      }
      window.parent.postMessage(data, '*')
    }

    const copilot = window.location.href.includes('copilot')

    if (copilot) {
      const data = {
        message: 'messageSent',
        time: new Date().toISOString()
      }
      window.parent.postMessage(data, '*')
    }

    const device = state.isMobileParent ? 'mobile' : 'desktop'
    message.metadata['device'] = device

    let messageData = {
      message: message.getUserResponsePayload(),
      metadata: message.metadata,
      session_id: sessionId
    }

    await socket.emit('user_uttered', messageData)
    message.sent = new Date()
  }

  async function emitNewUserLoginMessage(state, sessionId) {
    global.log.info(`emitNewUserLoginMessage ${sessionId}`)
    let message = {
      message: `/slot_set{"user_is_authenticated":${
        state.isUserLoggedIn ? 'true' : 'false'
      }}`,
      metadata: {},
      session_id: sessionId
    }

    addMetaData(message, state)

    await socket.emit('user_uttered', message)
  }

  async function emitNewUserSlotSetMessage(message, state) {
    addMetaData(message, state)
    await socket.emit('user_uttered', message)
  }

  async function emitStopMessage(payload, sessionId) {
    global.log.info(`emitStopMessage ${sessionId}`)
    let message = {
      session_id: sessionId
    }
    await socket.emit('llm_streaming_stop', message)
  }

  async function emitRegenerateMessage(payload, state, sessionId) {
    global.log.info(`emitRegenerateMessage ${sessionId}`)
    let message = {
      message: '/base.replay',
      metadata: { is_regenerated_from: payload },
      session_id: sessionId
    }

    addMetaData(message, state)
    await socket.emit('user_uttered', message)
  }

  async function emitUserStartedTyping() {
    await socket.emit('user_typing', true)
  }

  async function emitUserStoppedTyping() {
    await socket.emit('user_typing', false)
  }

  async function emitFeedbackMessage(state, rating, sessionId) {
    let message = {
      feedback: { ...rating },
      metadata: {},
      session_id: sessionId
    }
    addMetaData(message, state)
    global.log.info(`emitFeedbackMessage`)
    await socket.emit('user_message_feedback', message)
  }

  async function emitSentFeedback(state, payload, sessionId) {
    let message = {
      user_sent_feedback: payload.feedback,
      timestamp: payload.timestamp,
      metadata: {},
      session_id: sessionId
    }
    addMetaData(message, state)
    global.log.info(`emitSentFeedback`)
    await socket.emit('sent_feedback', message)
  }

  async function emitSessionRequest(language) {
    const sessionId = getSessionId()

    if (sessionId) {
      global.log.info(
        `emit session_request with previous session_id ${sessionId}`
      )
      socket.emit('session_request', {
        session_id: sessionId,
        conversation_id: conversationId ? conversationId.trim() : null,
        realm_tenant: global.realm_tenant,
        language: language
      })
    } else {
      global.log.info(`emit session_request without session_id`)
      socket.emit('session_request', {
        session_id: null,
        conversation_id: conversationId ? conversationId.trim() : null,
        realm_tenant: global.realm_tenant,
        language: language
      })
    }
  }

  async function emitRequestSemanticSuggestions(text) {
    global.log.info(`emitFeedbackMessage`)
    await socket.emit('request_semantic_suggestions', text)
  }

  async function emitNewUserRestartMessage(state, sessionId) {
    global.log.info(`emitNewUserRestartMessage ${sessionId}`)

    // when a user is seeing only three dots and clicks restart, we need to start a normal conversation
    // empty socket messageList indicate that no messages were yet received from backend
    // we need to start with greet message to properly start a conversation
    if (state.socket.messageList.length === 0) {
      emitGreetMessage(state, sessionId)
      return
    }

    let message = {
      message: '/session.restart',
      metadata: {},
      session_id: sessionId
    }

    addMetaData(message, state)

    await socket.emit('user_uttered', message)
  }

  function emitGreetMessage(state, sessionId) {
    const documentId = urlParams.get('document_id')
    const collectionId = urlParams.get('collection_id')
    const smartDoc = state.isCopilotChat
    let cookieUserId = retrieveCookieUserId()
    global.log.info(`emitGreetMessage with ${sessionId}`)
    const existing = getWithExpiry('currentSession', tenantHash)
    const parentDomain = getParentDomain()
    const urlWithoutParentDomain = parentUrlParam.replace(parentDomain, '')
    if (
      (existing !== sessionId || !existing) &&
      !isCRMLoginPage(urlWithoutParentDomain)
    ) {
      setWithExpiry('currentSession', sessionId, tenantHash)
    }

    let initialMessage

    let initialMessagePayload = {
      cookie_user_id: cookieUserId
    }

    if (createdBy) {
      initialMessagePayload['created_by'] = createdBy
    }

    if (documentId) {
      initialMessagePayload['dialogue_variables'] = {
        document_id: documentId
      }
    }

    if (collectionId) {
      initialMessagePayload['dialogue_variables'] = {
        collection_id: collectionId
      }
    }

    if (smartDoc && state.copilotAiConfig) {
      initialMessagePayload['dialogue_variables'] = {
        ...state.copilotAiConfig
      }
    }
    if (privacyAgreementAccepted) {
      initialMessagePayload['privacy_agreement'] = true
    }
    if (state.isUserLoggedIn) {
      initialMessagePayload['user_is_authenticated'] = true
    }

    if (state.language) {
      initialMessagePayload['language'] = state.language
    }

    if (estateId) {
      const messagePayload = `/appointment_estate{"appointment_estate_id":"${estateId}"}`

      initialMessage = new Message(
        constants.AUTHOR_BOT,
        sessionId,
        'Starte mit Terminvereinbarung'
      )
      initialMessage.payload = messagePayload
    }

    if (tenantId) {
      initialMessagePayload['authentication_tenant_id'] = tenantId
      // initialMessagePayload['user_is_authenticated'] = true
      if (contractId) {
        initialMessagePayload['aareon_contract_id'] = contractId
      }
      if (email) {
        initialMessagePayload['aareon_email'] = email
      }
      if (birthDate) {
        initialMessagePayload['aareon_birthdate'] = birthDate
      }
      if (referenceTenant) {
        initialMessagePayload['aareon_reference_tenant'] = referenceTenant
      }
    }

    initialMessage = new Message(
      constants.AUTHOR_BOT,
      sessionId,
      `/chitchat.greet${JSON.stringify(initialMessagePayload)}`
    )
    emitNewUserMessage(initialMessage, state).then(() => {
      // based on this postMessage the CRM sends an API Request
      // we only need this once per conversation id
      if (tenantId) {
        global.log.info(
          'Already got tenant info, not emitting sessionId postMessage'
        )
      } else if (state.shouldEmitAuthPostMessage) {
        setTimeout(() => {
          window.parent.postMessage(sessionId, '*')
        }, 1000)
      } else {
        state.shouldEmitAuthPostMessage = true
      }
    })
  }

  function addMetaData(message, state) {
    message.metadata['locale'] = state.locale
    message.metadata['user_logged_in'] = state.isUserLoggedIn
    message.metadata['user_language'] = state.language.toUpperCase()
    message.metadata['user_message'] = message.text
    message.metadata['realm_tenant'] = global.realm_tenant
  }

  function isOngoingSession() {
    return (
      getWithExpiry('currentSession', tenantHash) !== null &&
      sessionStorage.getItem('chatState')
    )
  }

  function isMobileParent(state) {
    return state.isMobileParent
  }

  // socket

  async function requestConversationPdf(state) {
    let sessionId = getSessionId(state)
    await socket.emit('request_conversation_pdf', {
      session_id: sessionId,
      realm_tenant: global.realm_tenant
    })
  }

  return store => {
    // NEW: Dedicated event for conversation title updates
    socket.on('copilot_conversation_title', data => {
      const postMessageData = {
        event: 'bot_uttered',
        data: {
          event: 'conversation_title_updated',
          conversation_id: data.conversation_id,
          title: data.title
        }
      }

      global.log.info(
        `Forwarding copilot_conversation_title to parent: ${JSON.stringify(
          data
        )}`
      )

      window.parent.postMessage(postMessageData, '*')
    })

    socket.on('bot_uttered', message => {
      console.log('[ChatPlugin] 🔔 bot_uttered received:', message)
      global.log.info(`Bot uttered ${JSON.stringify(message, null, '\t')}`)

      store.dispatch(actions.SET_SUGGESTIONS_ENABLED, true)

      if (store.state.isSmartDocChat) {
        const sourcesRegex = /Quellen:\s*<ul>(.*?)<\/ul>/g

        if (
          sourcesRegex.test(message['text']) &&
          messageHasSmartDocSegmentTooltips(message)
        ) {
          const smartdocSegmentTooltips = Object.keys(
            message['config']['data']['smartdoc_segment_tooltips']
          ).map(key => {
            return message['config']['data']['smartdoc_segment_tooltips'][key]
          })
          const data = {
            ...smartdocSegmentTooltips[0],
            message: 'scrollToFirstSource'
          }
          window.parent.postMessage(data, '*')
        }
      }
      let arrayWithSplitMessages = []
      if (message['text']) {
        const regex = /(?:\n\s*<br\s*\/?>\s*){2,}/gi
        arrayWithSplitMessages = message['text']
          .split(regex)
          .filter(bubble => bubble.trim() !== '')
      }

      let newMessages = []
      if (arrayWithSplitMessages.length > 1) {
        newMessages = getSeparateMessages(message, arrayWithSplitMessages)
      }
      if (newMessages.length > 0) {
        for (let i = 0; i < newMessages.length; i++) {
          const newMessage = newMessages[i]
          handleMessage(store, newMessage)
        }
      } else {
        handleMessage(store, message)
      }
    })

    socket.on('bot_uttered_part', message => {
      // global.log.info(`Bot uttered part ${JSON.stringify(message, null, '\t')}`)
      if (store.state.socket.regeneratedMessageFrom) {
        message.metadata['is_regenerated_from'] =
          store.state.socket.regeneratedMessageFrom
      }

      handleMessage(store, message)
    })

    socket.on('connect', () => {
      global.log.info(`connect`)
      if (global.isRestricted) {
        store.commit(mutations.IS_RESTRICTED, true)
        socket.disconnect()
        socket.destroy()
      }

      const collectionId = urlParams.get('collection_id')
      const chatMode = urlParams.get('mode')
      const isTutorialBot = chatMode === 'tutorial'
      if (collectionId) {
        setTimeout(() => store.dispatch(actions.SMART_DOC, true), 0)
      }
      window.parent.postMessage({ type: 'getParentWindowSize' }, '*')
      if (!isTutorialBot) {
        window.parent.postMessage(
          { message: 'updateSocketStatus', status: 'connected' },
          '*'
        )
      }
      const sessionId = getSessionId()
      if (sessionId) {
        window.parent.postMessage(
          { message: 'shareConversationId', sessionId: sessionId },
          '*'
        )
      }
      store.commit(mutations.CLEAR_MESSAGES)
      store.dispatch(actions.ENABLE_INPUT_FIELD, true)
      store.dispatch(actions.GET_DEVICE_INFO)
      if (store.state.canStartConversation) {
        emitSessionRequest(store.state.language).then(r => r)
      }
    })

    window.addEventListener('message', function(event) {
      if (event.data.type === 'parentWindowSize') {
        const mobile = event.data.innerWidth <= 768
        store.commit(mutations.SET_PARENT_WINDOW_SIZE, mobile)
      }
    })

    socket.on('close_restriction', () => {
      global.log.info(`close_restriction`)
      store.commit(mutations.IS_RESTRICTED, true)
      socket.disconnect()
      socket.destroy()
    })

    socket.on('authenticate', ioCookie => {
      global.log.info(`Authenticated ${ioCookie}`)
      if (!global.isRestricted) {
        store.commit(mutations.CLEAR_MESSAGES)
      }

      let data = {}

      data['language'] = store.state.language

      data['realm_tenant'] = global.realm_tenant
      if (tenantId) {
        data['aareon_tenant_id'] = tenantId
        if (contractId) {
          data['aareon_contract_id'] = contractId
        }
        if (email) {
          data['aareon_email'] = email
        }
        if (birthDate) {
          data['aareon_birthdate'] = birthDate
        }
        if (referenceTenant) {
          data['aareon_reference_tenant'] = referenceTenant
        }
      }
      socket.emit('request_config', data)
      if (isOngoingSession()) {
        let data = {
          type: 'ongoingSession',
          chatState: sessionStorage.getItem('chatState')
        }
        window.parent.postMessage(data, '*')
      }
    })

    socket.on('set_config', config => {
      global.log.info('set_config', config)
      if (config.hasOwnProperty('theme') && !store.state.isCopilotChat) {
        store.dispatch(actions.SET_THEME, config['theme'])
      }
      if (
        config.hasOwnProperty('should_auto_open') &&
        !!config['should_auto_open'] &&
        !isOngoingSession() &&
        !isMobileParent(store.state)
      ) {
        store.dispatch(actions.OPEN_WIDGET)
      }
      if (
        config.hasOwnProperty('reset_session_if_closed') &&
        !!config['reset_session_if_closed'] &&
        !(sessionStorage.getItem('chatState') === 'open')
      ) {
        sessionStorage.removeItem('currentSession')
      }
      if (
        (config.hasOwnProperty('proactive_message') &&
          // Proactive Message vorerst immer anzeigen, wenn chat zu ist und der User die +Seite aktualisiert oder wechselt
          // !isOngoingSession() &&
          !(sessionStorage.getItem('chatState') === 'open') &&
          !(
            config.hasOwnProperty('should_auto_open') &&
            !!config['should_auto_open'] &&
            !isMobileParent(store.state)
          )) ||
        (config.hasOwnProperty('proactive_message') &&
          sessionStorage.getItem('chatState') === 'closed' &&
          isOngoingSession())
      ) {
        // don't show proactive message if user has already declined it once in the session
        if (sessionStorage.getItem('chatInvitation') !== 'declined') {
          store.dispatch(
            actions.SHOW_PROACTIVE_MESSAGE,
            config['proactive_message']
          )
        }
      }
      if (config.hasOwnProperty('crm_languages') && isProbablyCRM()) {
        store.dispatch(actions.SET_ENABLED_LANGUAGES, config['crm_languages'])
      } else if (config.hasOwnProperty('languages')) {
        store.dispatch(actions.SET_ENABLED_LANGUAGES, config['languages'])
      }
      if (config.hasOwnProperty('file_upload_enabled')) {
        store.dispatch(
          actions.SET_FILE_UPLOAD_ENABLED,
          config['file_upload_enabled']
        )
      }
    })

    socket.on('set_theme', theme => {
      if (!store.state.isCopilotChat) {
        store.dispatch(actions.SET_THEME, theme)
      }
    })

    socket.on('proactive_message', proactiveMessage => {
      global.log.info('proactiveMessage', proactiveMessage)
      store.dispatch(actions.SHOW_PROACTIVE_MESSAGE, proactiveMessage)
    })

    socket.on('retrieved_previous_conversation', messages => {
      global.log.info(`retrieved_previous_conversation`)
      let sessionId = getSessionId(store.state)
      let parsedMessages = handlePreviousMessages(store, messages, sessionId)

      if (parsedMessages.length === 0) {
        emitGreetMessage(store.state, sessionId)
      } else {
        store.commit(mutations.ADD_MESSAGES, parsedMessages)
      }
    })

    socket.on('session_confirm', remoteId => {
      global.log.info(`Session confirm remoteId: ${remoteId}`)
      store.commit(mutations.SOCKET_ON_CONNECT)
      store.commit(mutations.STARTED_CONVERSATION)
      let sessionId = getSessionId(store.state)
      const existing = getWithExpiry('currentSession', tenantHash)
      const parentDomain = getParentDomain()

      if (parentUrlParam && dashboardUrl) {
        if (
          parentUrlParam.startsWith(dashboardUrl) &&
          parentUrlParam.includes('/agents/')
        ) {
          store.dispatch(actions.SET_AI_AGENT_CHAT, true)
        }
      }

      const urlWithoutParentDomain = parentUrlParam.replace(parentDomain, '')
      if (
        (existing !== sessionId || !existing) &&
        !isCRMLoginPage(urlWithoutParentDomain)
      ) {
        setWithExpiry('currentSession', sessionId, tenantHash)
      }
      if (
        (store.state.isUserLoggedIn &&
          !global.keycloak.hasRealmRole('dashboard-access')) ||
        conversationId
      ) {
        global.log.info(`retrieve_previous_conversation ${sessionId}`)
        socket.emit('retrieve_previous_conversation', {
          session_id: sessionId,
          realm_tenant: global.realm_tenant,
          language: store.state.language,
          isCopilot: store.state.isCopilotChat
        })
        store.commit(mutations.SOCKET_ON_SESSION_CONFIRM, sessionId)
      } else {
        if (sessionId) {
          global.log.info(`retrieve_previous_conversation ${sessionId}`)
          store.commit(mutations.SOCKET_ON_SESSION_CONFIRM, sessionId)
          socket.emit('retrieve_previous_conversation', {
            session_id: sessionId,
            realm_tenant: global.realm_tenant,
            language: store.state.language,
            isCopilot: store.state.isCopilotChat
          })
        } else {
          store.commit(mutations.SOCKET_ON_SESSION_CONFIRM, socket.id)
          socket.emit('retrieve_previous_conversation', {
            session_id: socket.id,
            realm_tenant: global.realm_tenant,
            language: store.state.language,
            isCopilot: store.state.isCopilotChat
          })
        }
      }
    })

    socket.on('reconnect_attempt', attempt => {
      global.log.info(`reconnect_attempt`)
      store.commit(mutations.SOCKET_ON_RECONNECT_ATTEMPTS, attempt)
    })

    socket.on('connect_error', error => {
      global.log.info(`connect_error ${error}`)
      store.commit(mutations.SOCKET_ON_CONNECT_ERROR, error)
    })

    socket.on('error', error => {
      global.log.info(`error ${error}`)
      store.commit(mutations.SOCKET_ON_ERROR, error)
    })

    socket.on('disconnect', reason => {
      global.log.info(`disconnect ${reason}`)
      global.isRestricted = true
      store.commit(mutations.SOCKET_ON_DISCONNECT, reason)
    })

    socket.on('join_confirm', reason => {
      global.log.info(`join_confirm Someone joined the room ${reason}`)
      if (
        reason &&
        reason[1] &&
        reason[1]['takeover'] &&
        reason[1]['takeover'] === true
      ) {
        global.log.info(`Someone took over the room ${reason}`)
        store.commit(mutations.SOCKET_ON_JOIN_CONFIRM, reason[1])
      }
    })

    socket.on('leave_confirm', reason => {
      global.log.info(`Someone left the room ${reason}`)
      store.commit(mutations.SOCKET_ON_LEAVE_CONFIRM, reason)
    })

    socket.on('web_search_progress', data => {
      try {
        const sid = getSessionId(store.state)
        if (
          !data ||
          sid == null ||
          String(data.room_id) !== String(sid)
        ) {
          return
        }
        if (data.phase === 'started') {
          store.commit(mutations.SOCKET_ON_WEB_SEARCH_PROGRESS, {
            phase: 'started'
          })
        } else {
          store.commit(mutations.SOCKET_ON_WEB_SEARCH_PROGRESS, null)
        }
      } catch (e) {
        global.log.warn('web_search_progress handler', e)
      }
    })

    socket.on('operator_typing', data => {
      store.commit(mutations.SOCKET_ON_OPERATOR_TYPING, data)
    })

    socket.on('set_conversation_pdf', data => {
      store.commit(mutations.SET_CONVERSATION_PDF_REQUESTED, data)
    })

    socket.on('document_indexed', data => {
      store.commit(mutations.SOCKET_ON_DOCUMENT_INDEXED, data)
    })

    store.subscribeAction({
      before: async (action, state) => {
        if (global.isRestricted) {
          store.commit(mutations.IS_RESTRICTED, true)
          socket.disconnect()
          socket.destroy()
        }

        let sessionId = getSessionId(state)

        if (sessionId !== null) {
          if (action.type === actions.EMIT_NEW_USER_MESSAGE) {
            await emitNewUserMessage(action.payload, state)
          } else if (action.type === actions.EMIT_USER_LOGIN_MESSAGE) {
            await emitNewUserLoginMessage(state, sessionId)
          } else if (action.type === actions.EMIT_USER_SLOT_SET_MESSAGE) {
            await emitNewUserSlotSetMessage(action.payload, state)
          } else if (action.type === actions.EMIT_STOP_MESSAGE) {
            await emitStopMessage(action.payload, sessionId)
          } else if (action.type === actions.EMIT_REGENERATE_MESSAGE) {
            await emitRegenerateMessage(action.payload, state, sessionId)
          } else if (action.type === actions.EMIT_FAKE_USER_LOGIN_MESSAGE) {
            state.isUserLoggedIn = true
            await emitNewUserLoginMessage(state, sessionId)
            state.isUserLoggedIn = false
          } else if (action.type === actions.EMIT_USER_RESTART_MESSAGE) {
            global.log.info('Emitting user restart message')
            state.shouldEmitAuthPostMessage = false
            sessionStorage.removeItem('currentSession')
            await emitNewUserRestartMessage(state, sessionId)
            if (state.isUserLoggedIn) {
              await emitNewUserLoginMessage(state, sessionId)
            }
            await emitGreetMessage(state, sessionId)
          } else if (action.type === actions.GET_CONVERSATION_PDF) {
            await requestConversationPdf(state)
          } else if (action.type === actions.CLOSE_CHAT_VIA_X) {
            // await emitTrackUi(state, action.payload, sessionId)
          }
        }

        if (action.type === actions.BOT_CONNECT) {
          if (!socket.connected) {
            socket.connect()
          }
        } else if (action.type === actions.BOT_DISCONNECT) {
          if (socket.connected) {
            socket.disconnect()
          }
        } else if (action.type === actions.START_CONVERSATION) {
          if (socket.connected && !state.isStartConversation) {
            await emitSessionRequest(store.state.language)
          }
        } else if (action.type === actions.EMIT_USER_MESSAGE_FEEDBACK) {
          if (socket.connected) {
            await emitFeedbackMessage(state, action.payload, sessionId)
          }
        } else if (action.type === actions.REQUEST_SEMANTIC_SUGGESTIONS) {
          if (!store.state.semanticSuggestionsEnabled) {
            global.log.info('Semantic suggestions disabled...')
            return
          }
          if (socket.connected) {
            await emitRequestSemanticSuggestions(action.payload)
          }
        } else if (action.type === actions.EMIT_SENT_FEEDBACK) {
          if (socket.connected) {
            await emitSentFeedback(state, action.payload, sessionId)
          }
        } else if (action.type === actions.ACCEPTED_INVITATION) {
          if (socket.connected) {
            if (!sessionId) {
              sessionId = socket.id
            }
            // await emitTrackUi(state, action.payload, sessionId)
          }
        } else if (action.type === actions.DECLINED_INVITATION) {
          if (socket.connected) {
            if (!sessionId) {
              sessionId = socket.id
            }
            // await emitTrackUi(state, action.payload, sessionId)
          }
        } else if (action.type === actions.CLICKED_TRIGGER_BUTTON) {
          if (socket.connected) {
            if (!sessionId) {
              sessionId = socket.id
            }
            // await emitTrackUi(state, action.payload, sessionId)
          }
        } else if (action.type === actions.SET_DEVICE_INFO) {
          if (socket.connected) {
            if (!sessionId) {
              sessionId = socket.id
            }
            // await emitTrackUi(state, action.payload, sessionId)
          }
        }
      },
      after: async (action, state) => {
        if (action.type === actions.TOGGLE_TYPING) {
          if (state.socket.userTyping) {
            await emitUserStartedTyping()
          } else {
            await emitUserStoppedTyping()
          }
        }
      }
    })
  }
}
